import { defineStore } from 'pinia';
import { SiteMapper } from '@/utils/mappers/SiteMapper';
import { defaultSiteConfiguration } from '@/data/defaultSiteConfiguration';
import { useSite } from '@/composables/api/useSite';
import OrderOption from '@/types/OrderOption';
import type { GraduationMode, Site, SiteApi } from '@/types/Site';
import { VariantEcom } from '@/types/Product';

const siteApi = useSite();

export const useSiteStore = defineStore('site', {
    state: () => ({
        site: {} as Site,
        configuration: defaultSiteConfiguration
    }),
    actions: {
        setSite(siteData: SiteApi) {
            const mappedSite = SiteMapper.getSiteFromData(siteData);
            if (!mappedSite.screenshotBackgroundColor) {
                mappedSite.screenshotBackgroundColor = mappedSite.sceneBackgroundColor;
            }
            this.site = mappedSite;
        },
        async setConfiguration(configurationUrl: string) {
            const { data, error } = await siteApi.getSiteConfig(configurationUrl);
            if (error.value) {
                console.error('Error while fetching site configuration:', error.value);
            }
            if (data.value) {
                const fetchedConfiguration = data.value;
                this.configuration = deepMergeJson(defaultSiteConfiguration, fetchedConfiguration);
            }
        },
        /** Valeur brute d'une feature (boolean|string) ou undefined si absente. */
        featureValue(featureName: string): boolean | string | undefined {
            return this.site?.features?.find((feature) => feature.name === featureName)?.value;
        },
        /**
         * Feature explicitement activée : absente => false (compat historique des
         * checks directs Themes/Import/...).
         */
        isFeatureEnabled(featureName: string): boolean {
            return this.featureValue(featureName) === true;
        },
        /**
         * Feature disponible côté outils : absente => ACTIVÉE (forward-compat). Une
         * feature nouvellement introduite n'est pas encore dans le tableau des
         * sites existants : on ne la masque que si l'admin l'a explicitement
         * décochée (value === false).
         */
        isFeatureAvailable(featureName: string): boolean {
            return this.featureValue(featureName) !== false;
        },
        /**
         * Mode VDP du site : 'always' | 'onDemand' | 'off'. Absent => 'onDemand'
         * (comportement historique : VDP visible seulement si le produit le demande).
         */
        vdpMode(): 'always' | 'onDemand' | 'off' {
            const raw = this.featureValue('Vdp');
            if (raw === 'always' || raw === 'off') {
                return raw;
            }
            return 'onDemand';
        },
        /**
         * Mode du champ quantité du configurateur : 'tiers' | 'tiersAndFree' | 'free'.
         * Porté par la feature 'QuantityMode' (comme 'Vdp'). Absent/invalide => 'tiers'
         * (paliers uniquement, comportement historique).
         */
        quantityMode(): 'tiers' | 'tiersAndFree' | 'free' {
            const raw = this.featureValue('QuantityMode');
            return raw === 'tiersAndFree' || raw === 'free' ? raw : 'tiers';
        },
        /**
         * Mode de proposition des graduations du site. Absent => 'auto'
         * (non-certifiées par défaut, certifiées seules si CertifiedPourline reçu).
         */
        graduationMode(): GraduationMode {
            const raw = this.site?.graduationMode;
            return raw === 'all' || raw === 'nonCertified' || raw === 'certified' ? raw : 'auto';
        },
        getOrderOptions(): OrderOption[] {
            return this.site?.options ?? [];
        },
        updateOrderOptionsWithPrices(variantsData: VariantEcom[]) {
            this.site.options.forEach((option) => {
                option.currentPrice = variantsData.find(
                    (variant) => variant.variantRef === option.ecommerceId
                )?.prices?.currentPrice;
            });
        }
    }
});
